/**
 * ¿Esta pestaña corre la ultima version de TORA?
 *
 * POR QUE HACE FALTA
 * ------------------
 * TORA es una SPA: el navegador baja el JavaScript una vez y despues solo
 * habla con `/api`. En el local la pestaña queda abierta el dia entero, y en
 * ese dia puede haber varios deploys. La pestaña sigue con el codigo de la
 * mañana contra un backend de la tarde: pantallas que no muestran lo nuevo,
 * arreglos que "no llegaron" y pedidos que el backend ya no entiende.
 *
 * COMO
 * ----
 * El build escribe `version.json` con un identificador propio (ver
 * vite.config.ts) y ese mismo identificador queda horneado en el bundle como
 * `__VERSION_TORA__`. Cada tanto, y cada vez que la pestaña vuelve a verse, se
 * pide `version.json`: si no coincide, hay una version nueva.
 *
 * Lo que se hace con eso lo decide el Shell: recargar al cambiar de pantalla
 * (el usuario ya se esta yendo de lo que tenia, no se pierde nada) y mostrar un
 * aviso con un boton para no esperar. NUNCA se recarga solo en medio de una
 * pantalla: se perderia una venta a medio cargar.
 */
import { useEffect, useState } from "react";

declare const __VERSION_TORA__: string;

/** La version con la que se buildeo este bundle. "dev" en desarrollo. */
export const VERSION_ACTUAL: string =
  typeof __VERSION_TORA__ === "string" ? __VERSION_TORA__ : "dev";

const CADA_MS = 2 * 60_000;

async function versionPublicada(): Promise<string | null> {
  try {
    const res = await fetch("/version.json", { cache: "no-store" });
    if (!res.ok) return null;
    const cuerpo = (await res.json()) as { version?: unknown };
    return typeof cuerpo.version === "string" ? cuerpo.version : null;
  } catch {
    // Sin red o en pleno deploy: se vuelve a probar en la proxima vuelta.
    return null;
  }
}

/** `true` cuando el servidor ya sirve una version distinta a la de esta pestaña. */
export function useHayVersionNueva(): boolean {
  const [nueva, setNueva] = useState(false);

  useEffect(() => {
    if (VERSION_ACTUAL === "dev" || nueva) return;

    let vivo = true;
    const revisar = async (): Promise<void> => {
      const publicada = await versionPublicada();
      if (vivo && publicada && publicada !== VERSION_ACTUAL) setNueva(true);
    };
    const alVolver = (): void => {
      if (document.visibilityState === "visible") void revisar();
    };

    const intervalo = window.setInterval(() => void revisar(), CADA_MS);
    document.addEventListener("visibilitychange", alVolver);
    window.addEventListener("focus", alVolver);
    return () => {
      vivo = false;
      window.clearInterval(intervalo);
      document.removeEventListener("visibilitychange", alVolver);
      window.removeEventListener("focus", alVolver);
    };
  }, [nueva]);

  return nueva;
}
